// The game context (spec/dashboard.md section 3.3): the game, its environments and branches, the caller's permissions,
// loaded once per game and shared by the shell and every page under it.
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { Branch, Environment, EnvironmentKind, Game, GamePermissions, PermissionName } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { queryCache, useQuery } from '../workspace/cache';
import { ErrorPage } from './ErrorPage';

export interface GameContextValue {
  org: string;
  game: Game;
  environments: Environment[];
  branches: Branch[];
  permissions: GamePermissions | null;
  /** true when the caller holds the permission (at the game level, or in the environment kind given) */
  can: (permission: PermissionName, env?: EnvironmentKind | string | null) => boolean;
  /** the permission missing for a control, or null when allowed (for `missingPermission` props) */
  missing: (permission: PermissionName, env?: EnvironmentKind | string | null) => string | null;
  refreshBranches: () => void;
  /** game-time offsets of dev and stage, for the time-zone labels */
  offsets: { dev?: number; stage?: number };
}

const GameContext = createContext<GameContextValue | null>(null);

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used inside GameProvider');
  return ctx;
}

export function useOptionalGame(): GameContextValue | null {
  return useContext(GameContext);
}

export function usePermissions() {
  const { can, missing, permissions } = useGame();
  return { can, missing, permissions };
}

export function gameKey(org: string, game: string, what: string): string {
  return `game:${org}/${game}:${what}`;
}

export function GameProvider({ children }: { children: ReactNode }) {
  const { org = '', game: gameSlug = '' } = useParams();
  const { client } = useAuth();
  const gameQ = useQuery(gameKey(org, gameSlug, 'game'), () => client.data(ops.getGame, { path: { org, game: gameSlug } }));
  const envsQ = useQuery(gameKey(org, gameSlug, 'envs'), () => client.data(ops.listEnvironments, { path: { org, game: gameSlug } }));
  const branchesQ = useQuery(gameKey(org, gameSlug, 'branches'), () => client.all((cursor) => client.data(ops.listBranches, { path: { org, game: gameSlug }, query: { limit: 200, cursor } })));
  const permsQ = useQuery(gameKey(org, gameSlug, 'permissions'), () => client.data(ops.getGamePermissions, { path: { org, game: gameSlug } }));

  const environments = useMemo(() => envsQ.data?.items ?? [], [envsQ.data]);
  const kindOf = useCallback(
    (env: EnvironmentKind | string | null | undefined): EnvironmentKind | null => {
      if (!env) return null;
      if (env === 'dev' || env === 'stage' || env === 'prod') return env;
      const found = environments.find((e) => e.slug === env || e.id === env);
      return found?.kind ?? null;
    },
    [environments],
  );
  const permissions = permsQ.data ?? null;
  const can = useCallback(
    (permission: PermissionName, env?: EnvironmentKind | string | null): boolean => {
      if (!permissions) return false;
      if (permissions.organization.includes(permission) || permissions.game.includes(permission)) return true;
      const kind = kindOf(env);
      if (kind) return permissions.environments[kind].includes(permission);
      // an environment permission asked without an environment: true when held anywhere
      return (['dev', 'stage', 'prod'] as const).some((k) => permissions.environments[k].includes(permission));
    },
    [permissions, kindOf],
  );
  const missing = useCallback((permission: PermissionName, env?: EnvironmentKind | string | null) => (can(permission, env) ? null : permission), [can]);
  const refreshBranches = useCallback(() => queryCache.invalidate(gameKey(org, gameSlug, 'branches')), [org, gameSlug]);
  const offsets = useMemo(() => {
    const out: { dev?: number; stage?: number } = {};
    for (const e of environments) if (e.kind === 'dev' || e.kind === 'stage') out[e.kind] = Number(e.gameTimeOffsetSeconds);
    return out;
  }, [environments]);

  const value = useMemo<GameContextValue | null>(
    () => (gameQ.data ? { org, game: gameQ.data, environments, branches: branchesQ.data ?? [], permissions, can, missing, refreshBranches, offsets } : null),
    [org, gameQ.data, environments, branchesQ.data, permissions, can, missing, refreshBranches, offsets],
  );

  const error = gameQ.error ?? envsQ.error ?? branchesQ.error ?? permsQ.error;
  if (error) {
    return <ErrorPage error={error instanceof ApiError ? error : new ApiError(0, 'unavailable', String(error), '')} retry={() => queryCache.invalidate(gameKey(org, gameSlug, ''))} />;
  }
  if (!value || !branchesQ.data || !permissions) return <p className="muted" style={{ padding: 24 }}>Loading…</p>;
  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}
